import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { globalTheme } from '@cld/component-theme';
import Checkbox from '@cld/checkbox';
import { GroupLayout, InlineFieldLayout, Label } from '@cld/form';
import TextField from '@cld/text-field';
import { typography } from '@cld/typography';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { messages } from '../../../i18n';
import { updateOptions } from '../../../actions';
import { useOptions } from '../../../selectors';
import { Section } from '../common/Section';

const InsertButtonContainer = styled.div`
  display: flex;
  align-items: center;
`;

const InsertButtonLabel = styled.div`
  ${typography.xs.regular};
  padding-right: ${globalTheme.spacing.sm};
  color: ${globalTheme.color.gray20};
`;

const InsertButtonField = styled(TextField)`
  flex-grow: 1;
`;

export const ClientSideDesign: FC = () => {
  const analytics = useAnalytics();

  const { formatMessage } = useIntl();
  const { removeHeader, insertCaption, inlineMode } = useOptions();

  const dispatch = useDispatch();

  const handleCheckboxChange = (id: 'inlineMode' | 'removeHeader', value: boolean, label: string) => {
    analytics('edit client side design', `${value ? 'check' : 'uncheck'} ${label}`);
    dispatch(updateOptions({ [id]: value }));
  };

  const handleInsertCaptionChange = (value: string) => {
    analytics('edit client side design', `change insert button caption to "${value}"`);
    dispatch(updateOptions({ insertCaption: value }));
  };

  return (
    <Section title={formatMessage(messages['demo.section.clientSideDesign'])}>
      <GroupLayout>
        <InlineFieldLayout
          label={
            <Label htmlFor="open-widget-inline">
              <FormattedMessage {...messages['demo.section.clientSideDesign.openMediaLibraryInline']} />
            </Label>
          }
        >
          <Checkbox
            value={inlineMode}
            onClick={() =>  handleCheckboxChange('inlineMode', !inlineMode, 'open media library inline')}
            id="open-widget-inline"
            inputProps={{
              'data-test': 'open-media-library-inline-checkbox'
            }}
          />
        </InlineFieldLayout>
        <InlineFieldLayout
          label={
            <Label htmlFor="remove-header">
              <FormattedMessage {...messages['demo.section.clientSideDesign.hideCloudinaryHeader']} />
            </Label>
          }
        >
          <Checkbox
            value={removeHeader}
            onClick={() => handleCheckboxChange('removeHeader', !removeHeader, 'hide the cloudinary header')}
            id="remove-header"
            inputProps={{
              'data-test': 'hide-the-cloudinary-header-checkbox'
            }}
          />
        </InlineFieldLayout>
        <InsertButtonContainer>
          <InsertButtonLabel>
            <FormattedMessage {...messages['demo.section.clientSideDesign.insertButtonCaption']} />
          </InsertButtonLabel>
          <InsertButtonField
            value={insertCaption}
            onChange={(newValue) => handleInsertCaptionChange(newValue)}
            type="text" size="xs"
            data-test="insert-button-caption-textfield-base"
            inputProps={{
              'data-test': 'insert-button-caption-textfield'
            }}
          />
        </InsertButtonContainer>
      </GroupLayout>
    </Section>
  );
};
